// Site-wide analytics event taxonomy (the `site_*` events).
//
// Mirrors createMemorialAnalytics: the transport (e.g. window.gtag) is injected
// by the host via FuneralUIProvider's `analytics={{ track }}` prop, so this
// package never touches window.gtag or process.env directly. `track(eventName,
// params)` is expected to be a no-op when analytics is disabled (host decides).
export function createSiteAnalytics(track) {
  const t = typeof track === "function" ? track : () => {}
  return {
    sectionView: sectionTitle =>
      t("site_section_view", { section_title: sectionTitle }),
    ctaClick: (label, destination) =>
      t("site_cta_click", { label, destination }),
    navClick: label => t("site_nav_click", { link_label: label }),
    contactSubmitted: () => t("site_contact_submitted"),
    preplanningSubmitted: () => t("site_preplanning_submitted"),
    videoPlay: title => t("site_video_play", { video_title: title }),
    downloadClick: label => t("site_download_click", { label }),
    backToTop: () => t("site_back_to_top"),
    historicalModalOpen: name =>
      t("site_historical_modal_open", { memorial_name: name }),
    newsletterSubmitted: () => t("site_newsletter_submitted"),
    // Paired with newsletterSubmitted, which fires on the ATTEMPT. Without an
    // outcome event a broken /api/subscribe reads as a 100% conversion rate.
    newsletterResult: outcome => t("site_newsletter_result", { outcome }),

    // ── Obituaries ─────────────────────────────────────────────────────────
    // The site's primary funnel — search, browse, click into a memorial — which
    // carried no instrumentation at all until now.
    //
    // Fired when the RESULTS land, not on submit, so the term and how many
    // matches it found travel together: a search that returns nothing is the
    // signal worth acting on (a name the funeral home spells differently, or a
    // memorial that was never published), and it is invisible if the event
    // fires before the count exists. A failed request fires obituaryLoadError
    // instead, so a broken proxy never masquerades as a zero-result search.
    obituarySearch: (term, resultCount) =>
      t("site_obituary_search", { search_term: term, result_count: resultCount }),
    obituaryResultClick: (memorialName, position, page) =>
      t("site_obituary_result_click", {
        memorial_name: memorialName,
        position,
        page,
      }),
    obituaryPageChange: page => t("site_obituary_page_change", { page }),
    obituaryLoadError: message => t("site_obituary_load_error", { message }),

    footerLinkClick: (label, destination) =>
      t("site_footer_link_click", { label, destination }),

    imageLightboxOpen: sectionTitle =>
      t("site_image_lightbox_open", { section_title: sectionTitle }),
    imageLightboxNav: direction =>
      t("site_image_lightbox_nav", { direction }),

    // `path` is the URL that missed. A 404 is only actionable if you can see
    // WHICH link is broken — most of these are inbound links from the site each
    // of these replaced.
    notFound: path => t("site_404", { path }),
    // The 404 pages are the only place a phone number is a real `tel:` link —
    // elsewhere the number is plain text, matching the sites these replaced, so
    // there is nothing to click. `context` says which page the call came from.
    phoneClick: context => t("site_phone_click", { context }),

    // The consent decision itself. Recorded on BOTH paths — under Consent Mode a
    // denial still delivers a cookieless ping, so the decline is measurable, and
    // knowing the accept rate is the only way to interpret every other number on
    // the site: a 60% accept rate means the rest of these events describe 60% of
    // visitors, not all of them.
    consentChoice: choice => t("site_consent_choice", { choice }),
  }
}
